/* eslint-disable unicorn/filename-case -- TODO rename later */
import { memo } from "react";

import { Close } from "~/assets/svg";
import { useThemeIconColors } from "~/theming/feature/use-theme-icon-colors";
import { cn } from "~/utils";

import IconButton from "../IconButton";

interface TextInputProps {
  ariaDescribedBy?: string;
  ariaLabel?: string;
  autoFocus?: boolean;
  dataTestId?: string;
  disabled?: boolean;
  icon?: React.JSX.Element;
  id?: string;
  isError?: boolean;
  placeholder?: string;
  required?: boolean;
  role?: string;
  size?: "sm" | "md";
  type?: "text" | "string" | "number";
  value?: string | number | undefined;
  variant?: "default" | "simple";
  onBlur?: () => void;
  onChange?: (_value: string) => void;
  onFocus?: () => void;
  className?: string;
}

/**
 * @deprecated Use `@istari-frontend/ui` instead.
 *
 * @example
 * import { Input } from "@istari-frontend/ui"
 */
const TextInput = memo(
  ({
    ariaDescribedBy,
    ariaLabel,
    autoFocus = false,
    dataTestId,
    disabled = false,
    icon,
    id,
    isError = false,
    placeholder = "",
    required,
    role,
    size = "md",
    type = "text",
    value = "",
    variant = "default",
    onBlur,
    onChange,
    onFocus,
    className = "",
  }: TextInputProps) => {
    const { muted } = useThemeIconColors();
    const bgColor = disabled ? "bg-gray-50" : "bg-white";
    const borderColor = isError ? "border-red-400" : "border-gray-200";
    const containerHeight = size === "sm" ? "h-8" : "h-10";
    const inputHeight = size === "sm" ? "h-6" : "h-8";
    const inputTextColor = disabled ? "text-gray-400" : "text-gray-600";
    const focusStyle = !disabled
      ? "shadow-[inset_0_1px_0_rgba(0,0,0,0.04)] focus-within:shadow-[inset_0_1px_0_rgba(0,0,0,0.06)]"
      : "";
    const ringStyle = !disabled
      ? isError
        ? "focus-within:ring-2 focus-within:ring-inset focus-within:ring-red-100"
        : "focus-within:ring-2 focus-within:ring-inset focus-within:ring-gray-100"
      : "";
    const borderStyle =
      variant === "simple"
        ? "border-b border-l-0 border-r-0 border-t-0 border-gray-200"
        : `rounded-sm border  hover:border-gray-300 focus-within:border-gray-300 disabled:hover:border-gray-100 ${borderColor}`;

    return (
      <div
        className={cn(
          "flex w-full items-center gap-x-2 p-2 font-semibold transition-all duration-150",
          containerHeight,
          borderStyle,
          bgColor,
          focusStyle,
          ringStyle,
          disabled && "opacity-50",
          className,
        )}
      >
        {icon || null}
        <input
          aria-describedby={ariaDescribedBy}
          aria-invalid={isError || undefined}
          aria-label={ariaLabel}
          autoFocus={autoFocus}
          className={cn(
            "w-full p-0 text-sm placeholder:text-sm placeholder:font-normal placeholder:text-gray-400 focus:outline-none",
            inputHeight,
            bgColor,
            inputTextColor,
          )}
          data-testid={dataTestId}
          disabled={disabled}
          id={id}
          placeholder={placeholder}
          required={required}
          type={type}
          value={value}
          onBlur={onBlur}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange?.(e.target.value)}
          onFocus={onFocus}
          role={role}
        />
        {value && (
          <IconButton
            dataTestId="clear-input-btn"
            disabled={disabled}
            icon={<Close color={muted} width="1rem" height="1rem" />}
            label="Clear"
            onClick={() => onChange?.("")}
            variant="transparent"
            size="sm"
          />
        )}
      </div>
    );
  },
);

export default TextInput;
